import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Lightbulb, MessageSquare, ThumbsUp, Send, Filter, Clock, 
  TrendingUp, CheckCircle, XCircle, Eye, Trash2, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Feedback = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, idea, feedback
  const [sortBy, setSortBy] = useState('newest'); // newest, popular
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [formType, setFormType] = useState('idea');
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');

  useEffect(() => {
    fetchFeedback();
  }, [filter, sortBy]);

  const fetchFeedback = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('type', filter);
      params.append('sort_by', sortBy);
      
      const response = await axios.get(`${API}/feedback?${params}`);
      setFeedbackList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to submit feedback');
      return;
    }
    
    if (formTitle.length < 5) {
      toast.error('Title must be at least 5 characters');
      return;
    }
    
    if (formDescription.length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        type: formType,
        title: formTitle,
        description: formDescription
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Thanks for your feedback!');
      setFormTitle('');
      setFormDescription('');
      setShowForm(false);
      fetchFeedback();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (feedbackId) => {
    if (!isAuthenticated) {
      toast.error('Please login to upvote');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/feedback/${feedbackId}/upvote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setFeedbackList(prev => prev.map(f => {
        if (f.id === feedbackId) {
          return {
            ...f,
            upvotes: response.data.upvoted ? f.upvotes + 1 : f.upvotes - 1,
            upvoted_by: response.data.upvoted 
              ? [...(f.upvoted_by || []), user.id]
              : (f.upvoted_by || []).filter(id => id !== user.id)
          };
        }
        return f;
      }));
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  const handleDelete = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    
    try {
      await axios.delete(`${API}/feedback/${feedbackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      fetchFeedback();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock, text: 'Pending' },
      reviewed: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Eye, text: 'Reviewed' },
      implemented: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle, text: 'Implemented' },
      declined: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle, text: 'Declined' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main className="pt-24 pb-12 px-3 sm:px-4 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <Lightbulb className="w-8 h-8 text-yellow-400" />
              Feedback & Ideas
            </h1>
            <p className="text-gray-400 mt-2">Share your challenge ideas and help us improve!</p>
          </div>
          
          <Button
            onClick={() => isAuthenticated ? setShowForm(!showForm) : toast.error('Please login to submit feedback')}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Submit {formType === 'idea' ? 'Idea' : 'Feedback'}
          </Button>
        </div>

        {/* Submit Form */}
        {showForm && (
          <Card className="glass-card border-purple-500/30 mb-8">
            <CardHeader>
              <CardTitle className="text-purple-400">Submit Your {formType === 'idea' ? 'Challenge Idea' : 'Feedback'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Type Toggle */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={formType === 'idea' ? 'default' : 'outline'}
                    onClick={() => setFormType('idea')}
                    className={formType === 'idea' ? 'bg-yellow-600' : ''}
                  >
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Challenge Idea
                  </Button>
                  <Button
                    type="button"
                    variant={formType === 'feedback' ? 'default' : 'outline'}
                    onClick={() => setFormType('feedback')}
                    className={formType === 'feedback' ? 'bg-blue-600' : ''}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Website Feedback
                  </Button>
                </div>
                
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder={formType === 'idea' ? 'e.g., Fortnite 1v1 Tournament' : 'e.g., Add dark mode toggle'}
                    className="bg-zinc-800 border-gray-700"
                    maxLength={100}
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <textarea
                    id="description"
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder={formType === 'idea' 
                      ? 'Describe your challenge idea in detail. What game? What rules? What makes it exciting?'
                      : 'Tell us what you think could be improved or what features you\'d like to see.'
                    }
                    className="w-full h-32 px-3 py-2 bg-zinc-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    maxLength={1000}
                  />
                  <p className="text-xs text-gray-500 mt-1">{formDescription.length}/1000 characters</p>
                </div>
                
                <div className="flex gap-2">
                  <Button type="submit" disabled={submitting} className="bg-green-600 hover:bg-green-700">
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Submit
                      </span>
                    )}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <div className="flex items-center gap-2 mr-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-400">Filter:</span>
          </div>
          {['all', 'idea', 'feedback'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-purple-600' : ''}
            >
              {f === 'all' && 'All'}
              {f === 'idea' && <><Lightbulb className="w-3 h-3 mr-1" /> Ideas</>}
              {f === 'feedback' && <><MessageSquare className="w-3 h-3 mr-1" /> Feedback</>}
            </Button>
          ))}
          
          <div className="flex items-center gap-2 ml-4">
            <span className="text-sm text-gray-400">Sort:</span>
            <Button
              variant={sortBy === 'newest' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('newest')}
              className={sortBy === 'newest' ? 'bg-blue-600' : ''}
            >
              <Clock className="w-3 h-3 mr-1" /> Newest
            </Button>
            <Button
              variant={sortBy === 'popular' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('popular')}
              className={sortBy === 'popular' ? 'bg-orange-600' : ''}
            >
              <TrendingUp className="w-3 h-3 mr-1" /> Popular
            </Button>
          </div>
        </div>

        {/* Feedback List */}
        {(() => {
          const safeFeedbackList = Array.isArray(feedbackList) ? feedbackList : [];
          if (loading) {
            return (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Loading feedback...</p>
              </div>
            );
          }
          if (safeFeedbackList.length === 0) {
            return (
              <Card className="glass-card border-gray-700">
                <CardContent className="py-12 text-center">
                  <Lightbulb className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-400 mb-2">No {filter !== 'all' ? filter + 's' : 'feedback'} yet</h3>
                  <p className="text-gray-500">Be the first to share your thoughts!</p>
                </CardContent>
              </Card>
            );
          }
          return (
            <div className="space-y-4">
              {safeFeedbackList.map((item) => (
                <Card 
                  key={item.id} 
                  className={`glass-card transition-all hover:border-opacity-50 ${
                    item.type === 'idea' ? 'border-yellow-500/30' : 'border-blue-500/30'
                  }`}
                >
                  <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Upvote Button */}
                    <div className="flex flex-col items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUpvote(item.id)}
                        className={`p-2 ${
                          item.upvoted_by?.includes(user?.id)
                            ? 'text-green-400 bg-green-500/20'
                            : 'text-gray-400 hover:text-green-400'
                        }`}
                      >
                        <ThumbsUp className="w-5 h-5" />
                      </Button>
                      <span className={`text-sm font-bold ${
                        item.upvotes > 0 ? 'text-green-400' : 'text-gray-500'
                      }`}>
                        {item.upvotes}
                      </span>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                          item.type === 'idea' 
                            ? 'bg-yellow-500/20 text-yellow-400' 
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {item.type === 'idea' ? <Lightbulb className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                          {item.type === 'idea' ? 'Idea' : 'Feedback'}
                        </span>
                        {getStatusBadge(item.status)}
                      </div>
                      
                      <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                      <p className="text-gray-400 text-sm mb-3 whitespace-pre-wrap">{item.description}</p>
                      
                      {item.admin_response && (
                        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mb-3">
                          <p className="text-xs text-purple-400 font-bold mb-1">Admin Response:</p>
                          <p className="text-sm text-gray-300">{item.admin_response}</p>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {item.user_profile_image && (
                            <img 
                              src={item.user_profile_image} 
                              alt="" 
                              className="w-6 h-6 rounded-full"
                            />
                          )}
                          <span className="text-sm text-gray-500">
                            {item.user_display_name} • {formatDate(item.created_at)}
                          </span>
                        </div>
                        
                        {user?.id === item.user_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(item.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );
      })()}
      </main>
    </div>
  );
};

export default Feedback;
